// Panoul de fațadă ca geometrie: dreptunghiul (u, z) al feței cu golurile
// decupate, extrudat cu grosimea lui și așezat pe normala feței. Separat de
// randor ca să poată fi PROBAT: orientarea unei cutii nu se vede în cod, se
// vede pe ecran — sau într-un test care întreabă încotro dau normalele ei.
import * as THREE from "three";
import { taie } from "../taietor";

/** Un gol pe un panou, în coordonatele panoului: `u` de la capătul `a`, `z`
 *  de la pardoseala nivelului. */
export interface GolPanou {
  u0: number; u1: number; z0: number; z1: number;
  /** GEAMUL PE COLȚ: gaura trece prin capătul panoului, și prin teșitura lui. */
  capat?: boolean;
}

/** Un panou de fațadă ca CUTIE: dreptunghiul `(u, z)` al feței, cu golurile
 *  decupate, extrudat cu grosimea lui și așezat la ieșirea lui din planul
 *  zidăriei (`depth_m` e fața exterioară, deci cutia începe la
 *  `depth_m − thickness_m`). Golurile rămân adâncituri reale: geamul stă în
 *  spatele finisajului, nu lipit pe el.
 *
 *  Orientarea: X local = de-a lungul feței, Y local = în sus, Z local = normala
 *  care iese din clădire. Ca reperul să rămână drept (X×Y = Z), direcția feței
 *  se ia astfel încât normala să fie ea rotită cu −90°; altfel geometria iese
 *  în oglindă și normalele ei privesc înăuntru. */
export function cutiePanou(a: number[], b: number[], n: number[], z0: number, z1: number,
                    depth: number, thickness: number, goluri: GolPanou[],
                    zBaza: number, kA = 0, kB = 0): THREE.BufferGeometry | null {
  let ax = a[0], ay = a[1], bx = b[0], by = b[1];
  const L = Math.hypot(bx - ax, by - ay);
  const H = z1 - z0;
  if (L < 0.05 || H < 0.005 || !(thickness > 0)) return null;
  let ux = (bx - ax) / L, uy = (by - ay) / L;
  let intors = false;
  if (uy * n[0] - ux * n[1] < 0) {
    [ax, ay, bx, by] = [bx, by, ax, ay];
    ux = -ux; uy = -uy; intors = true;
  }
  // găurile în reperul (eventual întors) al panoului, UNITE întâi (două care
  // se încalecă strică triangularea), apoi împărțite: interioare sau pe muchie
  const brute: number[][] = [];
  for (const g of goluri) {
    const u0 = intors ? L - g.u1 : g.u0, u1 = intors ? L - g.u0 : g.u1;
    const gz0 = g.z0 - z0, gz1 = g.z1 - z0;
    if (Math.min(u1, L) - Math.max(u0, 0) < 0.08 || Math.min(gz1, H) - Math.max(gz0, 0) < 0.08) continue;
    brute.push([u0, u1, gz0, gz1, g.capat ? 1 : 0]);
  }
  // GĂURILE SE SCAD CA POLIGOANE, TOATE, cu tăietorul de poligoane — nu ca
  // dreptunghiuri „unite" în cutia lor comună. `uneste` făcea din două găuri
  // care se încălecau parțial (fâșia verticală de lamele, cât etajul, și
  // brâul de la planșeu, cât fața) DREPTUNGHIUL lor comun — care înghițea și
  // ferestrele de sub brâu. Iar o gaură care se oprea la 1 cm de marginea
  // panoului ieșea din diferență ca INEL INTERIOR, pe care codul îl arunca:
  // panoul se întorcea plin, cu ferestrele acoperite (jumătate din ferestrele
  // etajelor cu fâșii, măsurat cu raze, 29.09.2026). Acum: conturul minus
  // reuniunea găurilor, iar fiecare poligon rezultat își păstrează inelele
  // interioare ca găuri ale formei.
  // …DAR NU LA UN CAPĂT TEȘIT (colț ieșind, kA/kB): acolo vârfurile cutiei se
  // mută după extrudare și o gaură dusă până în capăt le-ar strica. Gaura care
  // ajunge acolo se oprește la 4 cm de capăt (în REPERUL cutiei: dacă a și b
  // s-au schimbat între ele, `intors`, teșitura de jos e a lui b).
  const kJos = intors ? -kB : kA, kSus = intors ? -kA : kB;
  const decupate: number[][] = [];
  for (let [u0, u1, gz0, gz1, capat] of brute) {
    // …AFARĂ DE GEAMUL PE COLȚ: acolo gaura trece prin capăt; crestătura din
    // contur are vârfurile pe capăt, deci teșitura le mută odată cu restul și
    // buzele de sus și de jos ale găurii ies tăiate în unghi, ca ale feței vecine
    if (kJos && u0 <= 0.04 && !capat) u0 = 0.04;
    if (kSus && u1 >= L - 0.04 && !capat) u1 = L - 0.04;
    u0 = Math.max(u0, -0.5); u1 = Math.min(u1, L + 0.5);
    gz0 = Math.max(gz0, -0.5); gz1 = Math.min(gz1, H + 0.5);
    if (u1 - u0 < 0.08 || gz1 - gz0 < 0.08) continue;
    decupate.push([u0, u1, gz0, gz1]);
  }
  const aria = (r: number[][]) => Math.abs(r.reduce((a, p, i) => {
    const q = r[(i + 1) % r.length]; return a + p[0] * q[1] - q[0] * p[1]; }, 0)) / 2;
  let poligoane: number[][][][] = [[[[0, 0], [L, 0], [L, H], [0, H]]]];
  if (decupate.length) {
    try {
      const inchis = (r: number[][]) => [...r, r[0]] as [number, number][];
      const rez = taie().difference(
        [inchis(poligoane[0][0])] as never,
        ...decupate.map(([u0, u1, gz0, gz1]) =>
          [inchis([[u0, gz0], [u1, gz0], [u1, gz1], [u0, gz1]])] as never)) as unknown as number[][][][];
      poligoane = (rez ?? [])
        .map((poly) => poly.map((inel) => inel.slice(0, -1)).filter((inel) => inel.length >= 3 && aria(inel) > 0.001))
        .filter((poly) => poly.length && aria(poly[0]) > 0.01);
      if (!poligoane.length) return null;                 // panoul e tot gaură
    } catch {
      poligoane = [[[[0, 0], [L, 0], [L, H], [0, H]]]];
    }
  }
  const shapes: THREE.Shape[] = [];
  for (const [contur, ...inele] of poligoane) {
    const shape = new THREE.Shape();
    shape.moveTo(contur[0][0], contur[0][1]);
    for (const q of contur.slice(1)) shape.lineTo(q[0], q[1]);
    shape.closePath();
    for (const inel of inele) {
      const cale = new THREE.Path();
      cale.moveTo(inel[0][0], inel[0][1]);
      for (const q of inel.slice(1)) cale.lineTo(q[0], q[1]);
      cale.closePath();
      shape.holes.push(cale);
    }
    shapes.push(shape);
  }
  const geo = new THREE.ExtrudeGeometry(shapes, { depth: thickness, bevelEnabled: false });
  const off = depth - thickness;
  // CAPETELE TĂIATE ÎN UNGHI, ca la `profilPeFata`: la un colț ieșind cutia se
  // lungește pe `u` cu adâncimea ei de la zidărie (kB = +1 la b, kA = −1 la a),
  // deci cele două cutii vecine se ating pe planul bisector și pătratul de
  // colț nu mai rămâne gol. Vârfurile de la zidărie stau pe loc, cele de la
  // fața exterioară se mută: capacul devine un plan oblic. În reperul întors
  // capetele își schimbă locul, deci și factorii.
  if (kA || kB) {
    const jos = intors ? -kB : kA, sus = intors ? -kA : kB;
    const pos = geo.getAttribute("position");
    // NUMAI VÂRFURILE DE PE CAPĂT, nu toate din jumătatea lui: mutate toate,
    // se mutau și vârfurile GOLURILOR — fața de afară a fiecărui gol aluneca
    // pe `u` cu adâncimea finisajului (18 cm), deci golul ieșea strâmb în
    // adâncime: pe o parte șpaletul devenea o pană («pe triunghi, nu drept»),
    // pe cealaltă finisajul acoperea marginea ușii (Cătălin, 5.10.2026;
    // măsurat cu raze: o treime din golurile unui amplasament, pe orice față
    // care se termină într-un colț).
    // …ȘI UV-UL CU EL. UV-urile cutiei sunt în metri, pe `x` (fețele mari și
    // canturile orizontale) sau pe `y` (canturile verticale, v.
    // `WorldUVGenerator`). Mutat numai vârful, triunghiurile care pleacă din
    // colț peste toată fața, pe lângă goluri, se întindeau fiecare altfel —
    // rosturile plăcilor și ale cărămizii ieșeau frânte pe diagonalele lor
    // («placajul sau cărămida este strâmbă în unele locuri», Cătălin,
    // 8.10.2026). Pe triunghiurile al căror UV urmează `x` (toate trei
    // vârfurile au `uv.x = x`), `uv.x` se mută odată cu vârful.
    const uv = geo.getAttribute("uv");
    const vechi = new Float32Array(pos.count);
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i), d = off + pos.getZ(i);
      vechi[i] = x;
      if (x < 1e-4) pos.setX(i, x + jos * d);
      else if (x > L - 1e-4) pos.setX(i, x + sus * d);
    }
    if (uv && !geo.index) {
      for (let t = 0; t + 2 < pos.count; t += 3) {
        let peX = true, mutat = false;
        for (let k = t; k < t + 3; k++) {
          if (Math.abs(uv.getX(k) - vechi[k]) > 1e-5) { peX = false; break; }
          if (pos.getX(k) !== vechi[k]) mutat = true;
        }
        if (peX && mutat) for (let k = t; k < t + 3; k++) uv.setX(k, pos.getX(k));
      }
      uv.needsUpdate = true;
    }
    pos.needsUpdate = true;
  }
  const m = new THREE.Matrix4().makeBasis(
    new THREE.Vector3(ux, uy, 0), new THREE.Vector3(0, 0, 1), new THREE.Vector3(n[0], n[1], 0));
  m.setPosition(ax + n[0] * off, ay + n[1] * off, zBaza + z0);
  geo.applyMatrix4(m);
  return geo;
}

/** Dreptunghiurile `[u0, u1, z0, z1]` care se suprapun sau se ating, unite în
 *  cutia lor comună. Două găuri care se încalecă strică triangularea (earcut
 *  lasă crăpături — dungi oblice peste toată fața); o gaură puțin mai mare
 *  decât fereastra nu strică nimic. */
export function uneste(gauri: number[][]): number[][] {
  const out = gauri.map((g) => [...g]);
  let iar = true;
  while (iar) {
    iar = false;
    for (let i = 0; i < out.length && !iar; i++) {
      for (let j = i + 1; j < out.length; j++) {
        const a = out[i], b = out[j];
        if (a[0] <= b[1] && b[0] <= a[1] && a[2] <= b[3] && b[2] <= a[3]) {
          out[i] = [Math.min(a[0], b[0]), Math.max(a[1], b[1]), Math.min(a[2], b[2]), Math.max(a[3], b[3])];
          out.splice(j, 1);
          iar = true;
          break;
        }
      }
    }
  }
  return out;
}

/** Un relief de pe aceeași față, în coordonatele panoului: `u` de la capătul
 *  `a` al panoului, `z` de la pardoseala nivelului. */
export interface ReliefPeFata { u0: number; u1: number; z0: number; z1: number }

/** Cum se taie un panou de finisaj în jurul reliefurilor feței lui.
 *
 *  Un relief care ține CÂT panoul (un brâu pe toată fața) îl împarte pe
 *  verticală în felii, iar brâul umple fanta — nicio față în același plan cu
 *  alta. Un relief mai SCURT (glaful unei ferestre, un montant, o ramă) NU
 *  poate tăia fanta pe toată lungimea: așa ieșea o linie de gol peste toată
 *  fața, la cota glafului, peste tot unde nu era glaf. El devine o GAURĂ în
 *  panou, cu 1 cm mai mică decât el pe fiecare latură, pe care solidul lui
 *  (turnat de la zidărie) o umple — centimetrul rămas de finisaj stă
 *  înăuntrul solidului, nevăzut, și nu atinge nicio gaură vecină. */
export function taieDupaRelief(L: number, z0: number, z1: number, reliefuri: ReliefPeFata[]):
    { felii: [number, number][]; gauri: GolPanou[] } {
  let felii: [number, number][] = [[z0, z1]];
  const gauri: GolPanou[] = [];
  for (const r of reliefuri) {
    if (r.u1 <= 0.001 || r.u0 >= L - 0.001 || r.z1 <= z0 || r.z0 >= z1) continue;
    if (r.u0 <= 0.02 && r.u1 >= L - 0.02) {
      const urm: [number, number][] = [];
      for (const [a, b] of felii) {
        if (r.z1 <= a || r.z0 >= b) { urm.push([a, b]); continue; }
        if (r.z0 > a) urm.push([a, r.z0]);
        if (r.z1 < b) urm.push([r.z1, b]);
      }
      felii = urm;
    } else {
      gauri.push({ u0: r.u0 + 0.01, u1: r.u1 - 0.01, z0: r.z0 + 0.01, z1: r.z1 - 0.01 });
    }
  }
  return { felii: felii.filter(([a, b]) => b - a > 0.02), gauri };
}

/** Culoarea unui panou: cea din regulă, altfel una uzuală pentru felul lui. */
export const CULOARE_FEL: Record<string, number> = {
  plaster: 0xefe9dd, cladding: 0x6e6a66, brick: 0xa8583a, band: 0xffffff,
  sill: 0xe6e2d8, render: 0xcfcac0, insulation: 0xd8d8d8, glazing: 0x8fa6b8,
};



/** Inelul retras spre interior cu `d` metri, cu colțuri drepte.
 *
 *  Fiecare latură se mută pe normala EI spre interior, iar vârful nou e
 *  intersecția laturilor vecine mutate — deci un L rămâne L, cu colțul intrând
 *  în același loc. Nu e o scalare: o amprentă în L nu se poate scala fără să
 *  iasă din ea însăși. Pentru două laturi coliniare vârful se mută pur și
 *  simplu pe normală.
 *
 *  La ce servește: corpul din machetă se desenează retras cu câțiva centimetri
 *  de la planul zidăriei când clădirea are fațadă, ca nicio față a lui să nu
 *  cadă în planul unei fețe de panou. Două fețe coplanare se bat pe aceiași
 *  pixeli oricât de fină ar fi adâncimea; două fețe la 5 cm nu. */
/** Inelul fără vârfuri dublate consecutive și fără vârful de închidere repetat.
 *
 *  Un poligon din document vine des ÎNCHIS — ultimul punct e primul repetat.
 *  Pentru `insetInel` asta e o latură de lungime zero: vârful ei nu primea
 *  normală și rămânea PE LOC, neretras, iar fața corpului ieșea oblic de la
 *  colțul retras la cel neretras — până în planul pielii de fațadă, unde cele
 *  două se băteau pe o fâșie lângă colț. */
export function curataInel(ring: number[][]): number[][] {
  const out: number[][] = [];
  for (const p of ring) {
    const q = out[out.length - 1];
    if (q && Math.hypot(p[0] - q[0], p[1] - q[1]) < 1e-6) continue;
    out.push(p);
  }
  while (out.length > 1 && Math.hypot(out[0][0] - out[out.length - 1][0], out[0][1] - out[out.length - 1][1]) < 1e-6) out.pop();
  return out;
}

/** Inelul curățat (v. `curataInel`) și FĂRĂ VÂRFURI ÎN LINIE DREAPTĂ (abatere
 *  sub 1 cm). Numai pentru scobirea logiilor: `insetInel` păstrează vârfurile
 *  unu la unu cu inelul primit. Un vârf pe mijlocul frontonului rupea latura în
 *  două, iar terasele de capăt, cu câte un singur vârf pe fiecare bucată, nu mai
 *  erau «pe latură» — una se scobea din fața lungă, cealaltă din fronton,
 *  crestăturile se încălecau la colț și corpul ultimului etaj rămânea peste
 *  terasă (Demo-UTR6, Situația 3, 15.10.2026). */
function faraVarfuriInLinie(inel: number[][]): number[][] {
  const out = curataInel(inel);
  for (let schimbat = true; schimbat && out.length > 3;) {
    schimbat = false;
    for (let i = 0; i < out.length && out.length > 3; i++) {
      const a = out[(i + out.length - 1) % out.length], b = out[i], c = out[(i + 1) % out.length];
      // …și fără vârfuri la câțiva MILIMETRI de vecin (colțul triplat al
      // conturului ieșea 1 mm despărțit): latura lor de 1 mm nu mai e «pe
      // latură» pentru nimic, iar fâșia de corp de lângă ea rămânea în
      // picioare pe marginea terasei (C1, Demo-UTR6 Situația 3, 15.10.2026)
      if (Math.hypot(b[0] - a[0], b[1] - a[1]) < 0.005) { out.splice(i, 1); schimbat = true; i--; continue; }
      const L = Math.hypot(c[0] - a[0], c[1] - a[1]);
      if (L < 1e-6) continue;
      const abatere = Math.abs((c[0] - a[0]) * (b[1] - a[1]) - (c[1] - a[1]) * (b[0] - a[0])) / L;
      const intre = (b[0] - a[0]) * (c[0] - a[0]) + (b[1] - a[1]) * (c[1] - a[1]);
      if (abatere < 0.01 && intre > 0 && intre < L * L) { out.splice(i, 1); schimbat = true; i--; }
    }
  }
  return out;
}

/** Se taie inelul singur? (două laturi neînvecinate care se intersectează) */
function seAutointersecteaza(r: number[][]): boolean {
  const n = r.length;
  const cr = (a: number[], b: number[], c: number[]) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  for (let i = 0; i < n; i++) {
    const a = r[i], b = r[(i + 1) % n];
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue;
      const c = r[j], d = r[(j + 1) % n];
      const d1 = cr(a, b, c), d2 = cr(a, b, d), d3 = cr(c, d, a), d4 = cr(c, d, b);
      if (((d1 > 1e-9 && d2 < -1e-9) || (d1 < -1e-9 && d2 > 1e-9))
          && ((d3 > 1e-9 && d4 < -1e-9) || (d3 < -1e-9 && d4 > 1e-9))) return true;
    }
  }
  return false;
}

export function insetInel(inel: number[][], d: number): number[][] {
  const ring = curataInel(inel);
  const n = ring.length;
  if (n < 3 || !(d > 0)) return ring;
  let arie = 0;
  for (let i = 0, j = n - 1; i < n; j = i++) arie += ring[j][0] * ring[i][1] - ring[i][0] * ring[j][1];
  const semn = arie > 0 ? 1 : -1;                 // CCW: interiorul e la stânga
  const norm = (i: number): number[] | null => {
    const a = ring[i], b = ring[(i + 1) % n];
    const ex = b[0] - a[0], ey = b[1] - a[1], l = Math.hypot(ex, ey);
    if (l < 1e-9) return null;
    return [(-ey / l) * semn, (ex / l) * semn];
  };
  const out: number[][] = [];
  for (let i = 0; i < n; i++) {
    const p = ring[i];
    const n0 = norm((i + n - 1) % n), n1 = norm(i);
    if (!n0 || !n1) { out.push([p[0], p[1]]); continue; }
    // intersecția liniilor: p_prev + n0·d + t·e0  și  p + n1·d + s·e1
    const a0 = ring[(i + n - 1) % n];
    const e0 = [p[0] - a0[0], p[1] - a0[1]];
    const b1 = ring[(i + 1) % n];
    const e1 = [b1[0] - p[0], b1[1] - p[1]];
    const q0 = [a0[0] + n0[0] * d, a0[1] + n0[1] * d];
    const q1 = [p[0] + n1[0] * d, p[1] + n1[1] * d];
    const det = e0[0] * e1[1] - e0[1] * e1[0];
    // LATURI APROAPE ÎN LINIE (sau un ac, care se întoarce pe ea însăși): cele
    // două drepte retrase sunt aproape paralele, iar intersecția lor se
    // calcula împărțind la aproape zero. Un contur de atic deschis spre rost
    // avea vârfuri cu laturi de 6–7 m și sinusul unghiului de 2·10⁻⁵ — punctul
    // retras pleca la 1,6–5,9 km, iar macheta desena aticul ca grinzi spre
    // orizont (Demo-UTR6, 6.10.2026). Judecat pe SINUS, nu pe determinant: un
    // determinant mic e și al unor laturi scurte, perfect în unghi.
    const sin = det / ((Math.hypot(e0[0], e0[1]) * Math.hypot(e1[0], e1[1])) || 1);
    if (Math.abs(sin) < 1e-3) { out.push([p[0] + n1[0] * d, p[1] + n1[1] * d]); continue; }
    const t = ((q1[0] - q0[0]) * e1[1] - (q1[1] - q0[1]) * e1[0]) / det;
    const x = q0[0] + e0[0] * t, y = q0[1] + e0[1] * t;
    // …ȘI NICIUN VÂRF NU PLEACĂ MAI DEPARTE DE 10 × RETRAGEREA: la un colț
    // foarte ascuțit, retezat (ca un `bevel`), nu un vârf de kilometri
    if (Math.hypot(x - p[0], y - p[1]) > 10 * d) { out.push([p[0] + n1[0] * d, p[1] + n1[1] * d]); continue; }
    out.push([x, y]);
  }
  return out;
}


/** Conturul unui nivel cu LOGIILE scobite în el.
 *
 *  O logie stă cu o latură pe conturul corpului și intră în el. Corpul se
 *  desenează pe nivel cu o crestătură pe conturul ei — altfel volumul gri ar
 *  umple scobitura și pereții ei interiori ar rămâne îngropați. Crestătura se
 *  face fără operații booleene: pe latura conturului pe care stă logia se
 *  inserează cele două colțuri de pe contur și cele două din fund, în ordinea
 *  lor de-a lungul laturii. `retragere` mută conturul spre interior (v.
 *  `insetInel`) și lărgește crestătura cu tot atât, ca fețele corpului să nu
 *  cadă în planul panourilor logiei.
 */
export function conturCuLogii(inel: number[][], logii: number[][][], retragere: number): number[][] {
  const ring = faraVarfuriInLinie(inel);  // același inel ca în `insetInel`, vârf cu vârf
  const baza = retragere > 0 ? insetInel(ring, retragere) : ring;
  if (!logii.length || ring.length < 3) return baza;
  // LOGIA DE COLȚ — cea care stă pe DOUĂ laturi ale conturului, la un colț
  // intrând (M5 în colțul unui L/U) — nu e o crestătură pe o latură: e un L
  // care înfășoară colțul. Scobită ca dreptunghi de pe latura ei cea mai
  // lungă, jumătate din ea rămânea corp plin — un zid alb în logie, care
  // ascundea ușa (U-ul din Test-Catalin-v1, 29.09.2026). Ea se scade cu
  // adevărat, ca poligon; crestăturile simple merg pe drumul de până acum.
  // …IAR CE NU STĂ PE NICIO LATURĂ se scade tot ca poligon: terasa etajului
  // retras poate ieși cu 0,7 m dincolo de conturul planșeului lui (conturul
  // treptei e altul decât fața camerelor), deci niciun vârf al ei nu e «pe
  // latură» și crestătura n-avea de unde porni — corpul rămânea plin peste
  // terasă, «cubul de pe terasă» (Test-Catalin-v1, L-ul, 2.10.2026).
  const deColt: number[][][] = [], simple: number[][][] = [];
  for (const lg of logii) (peDouaLaturi(ring, lg) || !peOLatura(ring, lg) ? deColt : simple).push(lg);
  let out = simple.length ? conturCuCrestaturi(ring, baza, simple, retragere) : baza;
  // PLASA: crestăturile care tot ies încălecate (un contur care se taie
  // singur) se scad ca poligoane — altfel extrudarea umple o parte din terasă
  if (simple.length && seAutointersecteaza(out)) out = scadePoligoane(baza, simple);
  if (deColt.length) out = scadePoligoane(out, deColt);
  return out;
}

/** Are inelul logiei cel puțin două vârfuri pe UNA din laturile conturului
 *  (la cel mult 0,4 m de ea, pe lungimea ei) și intră în corp cu peste 0,3 m?
 *  Aceleași criterii ca în `conturCuCrestaturi`: ce trece de ele se scobește ca
 *  crestătură, restul se scade ca poligon. */
function peOLatura(ring: number[][], lg: number[][]): boolean {
  const n = ring.length;
  let arie = 0;
  for (let i = 0, j = n - 1; i < n; j = i++) arie += ring[j][0] * ring[i][1] - ring[i][0] * ring[j][1];
  const semn = arie > 0 ? 1 : -1;
  for (let i = 0; i < n; i++) {
    const p = ring[i], q = ring[(i + 1) % n];
    const ex = q[0] - p[0], ey = q[1] - p[1], L = Math.hypot(ex, ey);
    if (L < 1e-6) continue;
    const ux = ex / L, uy = ey / L, ox = uy * semn, oy = -ux * semn;
    let u0 = Infinity, u1 = -Infinity, dmax = 0, pe = 0;
    for (const v of lg) {
      const dx = v[0] - p[0], dy = v[1] - p[1];
      const u = dx * ux + dy * uy, d = -(dx * ox + dy * oy);
      if (Math.abs(d) <= 0.4 && u > -0.05 && u < L + 0.05) { pe++; u0 = Math.min(u0, u); u1 = Math.max(u1, u); }
      dmax = Math.max(dmax, d);
    }
    if (pe >= 2 && u1 - u0 > 0.3 && dmax > 0.3) return true;
  }
  return false;
}

/** Stă inelul logiei pe două laturi VECINE ale conturului care se întâlnesc
 *  într-un colț INTRÂND? (O terasă de capăt atinge și frontonul, și fața lungă,
 *  dar colțul lor e ieșind — aceea rămâne crestătura de până acum.) */
function peDouaLaturi(ring: number[][], lg: number[][]): boolean {
  const n = ring.length;
  let arie = 0;
  for (let i = 0, j = n - 1; i < n; j = i++) arie += ring[j][0] * ring[i][1] - ring[i][0] * ring[j][1];
  const ccw = arie > 0;
  const atinsa = (i: number): boolean => {
    const p = ring[i], q = ring[(i + 1) % n];
    const L = Math.hypot(q[0] - p[0], q[1] - p[1]);
    if (L < 1e-6) return false;
    const ux = (q[0] - p[0]) / L, uy = (q[1] - p[1]) / L;
    let pe = 0;
    for (const v of lg) {
      const dx = v[0] - p[0], dy = v[1] - p[1];
      const u = dx * ux + dy * uy, d = Math.abs(-dx * uy + dy * ux);
      if (d <= 0.4 && u > -0.05 && u < L + 0.05) pe++;
    }
    return pe >= 2;
  };
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;                       // laturile i și j se întâlnesc în ring[j]
    if (!atinsa(i) || !atinsa(j)) continue;
    const p = ring[i], q = ring[j], r = ring[(j + 1) % n];
    const cr = (q[0] - p[0]) * (r[1] - q[1]) - (q[1] - p[1]) * (r[0] - q[0]);
    const intrand = ccw ? cr < 0 : cr > 0;
    if (intrand) return true;
  }
  return false;
}

/** Inelul minus poligoanele date, ca operație de poligoane; rămâne bucata cea
 *  mai mare. Dacă scăderea nu dă un poligon curat, inelul rămâne cum era. */
function scadePoligoane(ring: number[][], poligoane: number[][][]): number[][] {
  try {
    const inchis = (r: number[][]): [number, number][] =>
      [...r.map((p) => [p[0], p[1]] as [number, number]), [r[0][0], r[0][1]]];
    // prin `taie()`: în browser modulul `polygon-clipping` vine cu `default`,
    // în teste cu funcțiile la vedere (v. `taietor.ts`)
    let rez = taie().difference([inchis(ring)] as never, ...poligoane.map((p) => [inchis(p)] as never)) as unknown as number[][][][];
    if (!rez || !rez.length) return ring;
    rez = rez.filter((poly) => poly.length && poly[0].length >= 4);
    if (!rez.length) return ring;
    const arie = (r: number[][]) => {
      let a = 0;
      for (let i = 0, j = r.length - 1; i < r.length; j = i++) a += r[j][0] * r[i][1] - r[i][0] * r[j][1];
      return Math.abs(a) / 2;
    };
    const mare = rez.reduce((m, poly) => (arie(poly[0]) > arie(m[0]) ? poly : m), rez[0]);
    return curataInel(mare[0].slice(0, -1).map((p) => [p[0], p[1]]));
  } catch {
    return ring;
  }
}

function conturCuCrestaturi(ring: number[][], baza: number[][], logii: number[][][], retragere: number): number[][] {
  const n = ring.length;
  let arie = 0;
  for (let i = 0, j = n - 1; i < n; j = i++) arie += ring[j][0] * ring[i][1] - ring[i][0] * ring[j][1];
  const semn = arie > 0 ? 1 : -1;                 // CCW: interiorul e la stânga
  // Laturile, cu reperul lor: direcția și normala LATURII spre exterior (nu a
  // vârfului: la un colț oblic vârful se retrage pe bisectoare, și normala
  // dedusă din el ar fi strâmbă).
  const laturi = ring.map((p, i) => {
    const q = ring[(i + 1) % n];
    const ex = q[0] - p[0], ey = q[1] - p[1], L = Math.hypot(ex, ey);
    const ux = L > 1e-6 ? ex / L : 0, uy = L > 1e-6 ? ey / L : 0;
    return { p, L, ux, uy, ox: uy * semn, oy: -ux * semn };
  });
  // FIECARE SCOBITURĂ, DE PE O SINGURĂ LATURĂ: cea pe care stă cu latura ei
  // cea mai lungă. O terasă de capăt atinge conturul și pe fronton (7,9 m), și
  // pe fața lungă (4,4 m); scobită de pe amândouă, aceeași bucată de corp era
  // tăiată de două ori și conturul se autointersecta — rămânea o felie de
  // corp cât etajul, în mijlocul terasei.
  const crestPeLatura: { u0: number; u1: number; d: number }[][] = laturi.map(() => []);
  for (const lg of logii) {
    let best: { i: number; u0: number; u1: number; d: number } | null = null;
    for (let i = 0; i < laturi.length; i++) {
      const lat = laturi[i];
      if (lat.L < 1e-6) continue;
      let u0 = Infinity, u1 = -Infinity, dmax = 0, peLatura = 0;
      for (const v of lg) {
        const dx = v[0] - lat.p[0], dy = v[1] - lat.p[1];
        const u = dx * lat.ux + dy * lat.uy, d = -(dx * lat.ox + dy * lat.oy);   // d > 0 = spre interior
        if (Math.abs(d) <= 0.4 && u > -0.05 && u < lat.L + 0.05) { peLatura++; u0 = Math.min(u0, u); u1 = Math.max(u1, u); }
        dmax = Math.max(dmax, d);
      }
      if (peLatura >= 2 && u1 - u0 > 0.3 && dmax > 0.3 && (best === null || u1 - u0 > best.u1 - best.u0)) {
        best = { i, u0, u1, d: dmax };
      }
    }
    if (best !== null) {
      const b: { i: number; u0: number; u1: number; d: number } = best;
      crestPeLatura[b.i].push({ u0: b.u0, u1: b.u1, d: b.d });
    }
  }
  // scobiturile de pe fiecare latură, în ordine, iar cele care se ating sau
  // se încalecă (cele două jumătăți ale unei terase, cu despărțitorul între
  // ele) se UNESC într-una, la adâncimea celei mai adânci
  const unite = crestPeLatura.map((crest) => {
    const out: { u0: number; u1: number; d: number }[] = [];
    for (const c of crest.sort((a, b) => a.u0 - b.u0)) {
      const ultima = out[out.length - 1];
      // …și cu un ROST de până la 45 cm între ele (despărțitorul teraselor,
      // când poligoanele lor nu-l cuprind)
      if (ultima && c.u0 <= ultima.u1 + 0.45) {
        ultima.u1 = Math.max(ultima.u1, c.u1); ultima.d = Math.max(ultima.d, c.d);
      } else out.push({ ...c });
    }
    return out;
  });
  // O LATURĂ RETRASĂ ÎN ÎNTREGIME (o terasă pe tot frontonul) își pierde și
  // colțurile: altfel fiecare colț vechi rămânea ca o lamă de corp de 5 cm,
  // dusă până la linia retrasă
  // o scobitură care ajunge la 45 cm de capătul laturii (parapetul terasei
  // rămas în afara poligonului ei) se ÎNTINDE până la capăt: altfel rămânea o
  // lamă de corp cât parapetul, pe toată înălțimea
  for (let i = 0; i < n; i++) {
    for (const c of unite[i]) {
      if (c.u0 <= 0.45) c.u0 = 0;
      if (c.u1 >= laturi[i].L - 0.45) c.u1 = laturi[i].L;
    }
  }
  const plin = unite.map((u, i) => u.length === 1
    && u[0].u0 - retragere <= 0.02 && u[0].u1 + retragere >= laturi[i].L - 0.02);
  const out: number[][] = [];
  for (let i = 0; i < n; i++) {
    const lat = laturi[i];
    if (!plin[i] && !plin[(i + n - 1) % n]) out.push(baza[i]);
    if (lat.L < 1e-6) continue;
    const pe = (u: number, dd: number): number[] =>
      [lat.p[0] + lat.ux * u - lat.ox * dd, lat.p[1] + lat.uy * u - lat.oy * dd];
    for (const c of unite[i]) {
      const d = c.d + retragere;
      // …capetele ei stau retrase și pe laturile vecine, ca restul inelului
      if (plin[i]) { out.push(pe(retragere, d), pe(lat.L - retragere, d)); continue; }
      const laInceput = c.u0 - retragere <= 0.02, laCapat = c.u1 + retragere >= lat.L - 0.02;
      const u0 = Math.max(0.01, c.u0 - retragere), u1 = Math.min(lat.L - 0.01, c.u1 + retragere);
      out.push(...(laInceput ? [pe(0, d)] : [pe(u0, retragere), pe(u0, d)]),
               ...(laCapat ? [pe(lat.L, d)] : [pe(u1, d), pe(u1, retragere)]));
    }
  }
  return curataInel(out);
}


// ---------------------------------------------------------------------------
// Texturi PROCEDURALE pe materiale de fațadă.
//
// Cheia e `material` a panoului (cheia de cost), nu culoarea: cărămida aparentă
// e țesătură de cărămizi oricare i-ar fi nuanța, lamelele sunt dungi
// verticale, tencuiala are granulație. Desenate pe canvas, nu descărcate: nimic
// de licențiat, nimic de încărcat, iar o textură pe metru e aceeași la orice
// scară, fiindcă UV-urile cutiei sunt în METRI (forma se extrudează în
// coordonatele feței), deci `repeat = 1 / latura_m`.
// ---------------------------------------------------------------------------

export interface TexturaFatada {
  map: THREE.Texture;
  /** Cât acoperă o repetare, în metri. */
  latura_m: number;
  roughness: number;
  metalness: number;
}

const _cache = new Map<string, TexturaFatada | null>();

function panza(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] | null {
  if (typeof document === "undefined") return null;
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const ctx = c.getContext("2d");
  return ctx ? [c, ctx] : null;
}

function amesteca(hex: number, k: number): string {
  const r = (hex >> 16) & 255, g = (hex >> 8) & 255, b = hex & 255;
  const f = (v: number) => Math.max(0, Math.min(255, Math.round(v * k)));
  return `rgb(${f(r)},${f(g)},${f(b)})`;
}

/** Zgomot determinist pe (x, y): același desen la fiecare încărcare. */
function zgomot(x: number, y: number): number {
  const v = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
  return v - Math.floor(v);
}

/** PIETRELE ȘI LEMNELE cu desen propriu (Cătălin, 5.10.2026: «mai multe
 *  texturi de piatră și lemn, să aibă din ce alege»). Cheia e cheia de cost a
 *  materialului; culoarea rămâne a regulii, deci fiecare desen se poate cere în
 *  orice nuanță. Aceleași chei, cu aceleași dimensiuni, în randarea din Blender
 *  (`tools/blender/canonical_roundtrip.py: TEXTURI_FATADA`) și în lista
 *  agentului (`fatada/prompt.py`) — `panou3d.test.ts` le ține în pas. */
export const MATERIALE_PIATRA = ["placaj_piatra", "piatra_travertin", "piatra_granit",
                                 "piatra_ardezie", "piatra_zidita"] as const;
export const MATERIALE_LEMN = ["placaj_hpl", "lemn_ingust", "lemn_vertical", "lemn_riflaj",
                               "lemn_panouri"] as const;

/** Rânduri de piese pe o pânză pătrată: `inaltimi` și `lungimi` în metri,
 *  însumate la `latura` (ca desenul să se repete fără cusătură). */
function zidarie(ctx: CanvasRenderingContext2D, s: number, latura: number, culoare: number,
                 inaltimi: number[], lungimi: number[][], rostM: number, variatie: number) {
  const px = s / latura;
  let y = 0;
  inaltimi.forEach((h, r) => {
    const lst = lungimi[r % lungimi.length];
    let x = -((r * 0.37) % 1) * lst[0] * px;
    for (let k = 0; x < s; k++) {
      const w = lst[k % lst.length] * px;
      ctx.fillStyle = amesteca(culoare, 1 - variatie + 2 * variatie * zgomot(k * 3.7 + r * 11.3, r * 5.1));
      ctx.fillRect(x + rostM * px / 2, y + rostM * px / 2, w - rostM * px, h * px - rostM * px);
      x += w;
    }
    y += h * px;
  });
}

/** Scânduri sau șipci: `pas` lățimea unei piese cu rost cu tot, `rost` golul
 *  dintre ele (în umbră), pe orizontală sau pe verticală; `fibra` = câte linii
 *  de fibră pe piesă. */
function scanduri(ctx: CanvasRenderingContext2D, s: number, latura: number, culoare: number,
                  pas: number, rost: number, vertical: boolean, fibra: number, variatie: number) {
  const px = s / latura, n = Math.round(latura / pas);
  for (let r = 0; r < n; r++) {
    const ton = 1 - variatie + 2 * variatie * zgomot(r * 7.1, 3.3);
    const a0 = r * pas * px + rost * px / 2, lat = (pas - rost) * px;
    const g = vertical ? ctx.createLinearGradient(a0, 0, a0 + lat, 0) : ctx.createLinearGradient(0, a0, 0, a0 + lat);
    g.addColorStop(0, amesteca(culoare, ton * 1.08)); g.addColorStop(0.5, amesteca(culoare, ton));
    g.addColorStop(1, amesteca(culoare, ton * 0.9));
    ctx.fillStyle = g;
    if (vertical) ctx.fillRect(a0, 0, lat, s); else ctx.fillRect(0, a0, s, lat);
    ctx.fillStyle = amesteca(culoare, ton * 0.86);
    for (let k = 0; k < fibra; k++) {
      const p = a0 + lat * (0.18 + 0.64 * (k + zgomot(k, r)) / Math.max(1, fibra));
      if (vertical) ctx.fillRect(p, 0, 1, s); else ctx.fillRect(0, p, s, 1);
    }
  }
}

function desenulMaterialului(m: string, culoare: number): TexturaFatada | null {
  const p = panza(512, 512);
  if (!p) return null;
  const [c, ctx] = p;
  const s = 512;
  const gata = (latura_m: number, roughness: number, metalness = 0): TexturaFatada =>
    ({ map: new THREE.CanvasTexture(c), latura_m, roughness, metalness });
  switch (m) {
    case "piatra_travertin": {
      // TRAVERTIN: plăci de 90 × 45 în țesătură, cu vinișoarele lui orizontale
      ctx.fillStyle = amesteca(culoare, 0.84); ctx.fillRect(0, 0, s, s);
      zidarie(ctx, s, 1.8, culoare, [0.45, 0.45, 0.45, 0.45], [[0.9, 0.9]], 0.005, 0.04);
      for (let k = 0; k < 90; k++) {
        const y = zgomot(k, 1.7) * s, x = zgomot(k, 9.1) * s, l = (0.15 + 0.5 * zgomot(k, 4.2)) * s;
        ctx.fillStyle = amesteca(culoare, zgomot(k, 2.2) > 0.5 ? 0.9 : 1.07);
        ctx.fillRect(x, y, l, 1); ctx.fillRect(x - s, y, l, 1);
      }
      return gata(1.8, 0.6);
    }
    case "piatra_granit": {
      // GRANIT FIAMAT: plăci de 60 × 60 cu rost continuu, bob mărunt «sare și piper»
      const img = ctx.createImageData(s, s);
      const r = (culoare >> 16) & 255, g = (culoare >> 8) & 255, b = culoare & 255;
      for (let i = 0; i < s * s; i++) {
        const z = zgomot(i % s, Math.floor(i / s));
        const k = z > 0.93 ? 1.22 : z < 0.08 ? 0.72 : 0.95 + 0.1 * z;
        img.data[i * 4] = Math.min(255, r * k); img.data[i * 4 + 1] = Math.min(255, g * k);
        img.data[i * 4 + 2] = Math.min(255, b * k); img.data[i * 4 + 3] = 255;
      }
      ctx.putImageData(img, 0, 0);
      ctx.fillStyle = amesteca(culoare, 0.7);
      for (const q of [0, s / 2]) { ctx.fillRect(q, 0, 2, s); ctx.fillRect(0, q, s, 2); }
      return gata(1.2, 0.45, 0.04);
    }
    case "piatra_ardezie": {
      // ARDEZIE ÎN FÂȘII: rânduri înguste de înălțimi diferite, lungimi amestecate
      ctx.fillStyle = amesteca(culoare, 0.6); ctx.fillRect(0, 0, s, s);
      zidarie(ctx, s, 1.2, culoare, [0.10, 0.06, 0.14, 0.08, 0.12, 0.05, 0.15, 0.07, 0.11, 0.09, 0.13, 0.10],
              [[0.4, 0.5, 0.3], [0.6, 0.25, 0.35], [0.3, 0.45, 0.45]], 0.004, 0.13);
      return gata(1.2, 0.85);
    }
    case "piatra_zidita": {
      // PIATRĂ ZIDITĂ: blocuri mari în rânduri inegale, cu rost adânc — soclu, parter
      ctx.fillStyle = amesteca(culoare, 0.55); ctx.fillRect(0, 0, s, s);
      zidarie(ctx, s, 1.6, culoare, [0.25, 0.35, 0.2, 0.3, 0.25, 0.25],
              [[0.5, 0.35, 0.45, 0.3], [0.6, 0.4, 0.6], [0.3, 0.5, 0.35, 0.45]], 0.014, 0.11);
      return gata(1.6, 0.92);
    }
    case "lemn_ingust":
      // ȘIPCI ORIZONTALE ÎNGUSTE (7,5 cm) cu rost deschis: placarea «rhombus»
      ctx.fillStyle = amesteca(culoare, 0.4); ctx.fillRect(0, 0, s, s);
      scanduri(ctx, s, 1.2, culoare, 0.075, 0.012, false, 1, 0.09);
      return gata(1.2, 0.72);
    case "lemn_vertical":
      // SCÂNDURI VERTICALE de 12 cm cu rost de umbră
      ctx.fillStyle = amesteca(culoare, 0.45); ctx.fillRect(0, 0, s, s);
      scanduri(ctx, s, 1.2, culoare, 0.12, 0.008, true, 3, 0.1);
      return gata(1.2, 0.7);
    case "lemn_riflaj":
      // RIFLAJ: șipci verticale de 4 cm la 6 cm, cu umbră adâncă între ele
      ctx.fillStyle = amesteca(culoare, 0.28); ctx.fillRect(0, 0, s, s);
      scanduri(ctx, s, 0.6, culoare, 0.06, 0.02, true, 1, 0.07);
      return gata(0.6, 0.7);
    case "lemn_panouri":
      // PANOURI MARI cu decor de lemn: 1,2 m lățime, fibră verticală continuă, rost fin
      ctx.fillStyle = amesteca(culoare, 0.5); ctx.fillRect(0, 0, s, s);
      scanduri(ctx, s, 2.4, culoare, 1.2, 0.008, true, 26, 0.035);
      ctx.fillStyle = amesteca(culoare, 0.5); ctx.fillRect(0, 0, s, 2);       // rostul orizontal, la 2,4 m
      return gata(2.4, 0.6);
    default:
      return null;
  }
}

/** GRANULAȚIA unei plăci de calepinaj: zgomot alb-cenușiu fin, înmulțit cu
 *  culoarea plăcii (și cu tonul ei, pe vârfuri). Fără rosturi desenate: pe un
 *  panou cu calepinaj rosturile sunt adevărate, între plăci — desenate și în
 *  textură, s-ar fi dublat cu o grilă care nu se potrivește cu a lor. */
let _granulatie: THREE.Texture | null = null;
export function texturaGranulatie(): THREE.Texture | null {
  if (_granulatie) return _granulatie;
  const p = panza(256, 256);
  if (!p) return null;
  const [c, ctx] = p;
  const img = ctx.createImageData(256, 256);
  for (let i = 0; i < 256 * 256; i++) {
    const v = Math.round(255 * (0.93 + 0.07 * zgomot(i % 256, Math.floor(i / 256))));
    img.data[i * 4] = img.data[i * 4 + 1] = img.data[i * 4 + 2] = v;
    img.data[i * 4 + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(2, 2);                 // o repetare = 0,5 m
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  _granulatie = t;
  return t;
}

/** Textura unui material de fațadă, sau `null` când e o culoare plată. */
export function texturaMaterial(material: string | undefined, culoare: number): TexturaFatada | null {
  const cheie = `${material ?? ""}|${culoare}`;
  if (_cache.has(cheie)) return _cache.get(cheie)!;
  let out: TexturaFatada | null = null;
  const m = material ?? "";
  // întâi desenele cu cheie proprie: «piatra_ardezie» ar cădea altfel pe piatra
  // generică, iar «lemn_riflaj» pe scândurile orizontale
  if ((out = desenulMaterialului(m, culoare))) {
    // gata
  } else if (/caramida|brick/.test(m)) {
    // țesătură în lungime: cărămidă 25 × 6,5 cm, rost 1 cm; o repetare = 1 × 1 m
    const p = panza(512, 512);
    if (p) {
      const [c, ctx] = p;
      const s = 512;                       // px pe metru
      ctx.fillStyle = amesteca(culoare, 1.25); ctx.fillRect(0, 0, s, s);  // rostul, deschis
      const bw = 0.25 * s, bh = 0.065 * s, rost = 0.01 * s;
      for (let r = 0, y = 0; y < s; r++, y += bh + rost) {
        const dec = (r % 2) * (bw + rost) / 2;
        for (let x = -bw; x < s + bw; x += bw + rost) {
          ctx.fillStyle = amesteca(culoare, 0.86 + 0.28 * zgomot(x + dec, y));
          ctx.fillRect(x + dec, y, bw, bh);
        }
      }
      const t = new THREE.CanvasTexture(c);
      out = { map: t, latura_m: 1.0, roughness: 0.95, metalness: 0 };
    }
  } else if (/lamele|slat/.test(m)) {
    // lamele verticale de 6 cm cu rost de 4 cm; o repetare = 0,5 m
    const p = panza(256, 256);
    if (p) {
      const [c, ctx] = p;
      ctx.fillStyle = amesteca(culoare, 0.55); ctx.fillRect(0, 0, 256, 256);   // umbra dintre lamele
      const lw = 0.06 / 0.5 * 256, pas = 0.10 / 0.5 * 256;
      for (let x = 0; x < 256; x += pas) {
        const g = ctx.createLinearGradient(x, 0, x + lw, 0);
        g.addColorStop(0, amesteca(culoare, 1.25)); g.addColorStop(0.5, amesteca(culoare, 1.0));
        g.addColorStop(1, amesteca(culoare, 0.7));
        ctx.fillStyle = g; ctx.fillRect(x, 0, lw, 256);
      }
      const t = new THREE.CanvasTexture(c);
      out = { map: t, latura_m: 0.5, roughness: 0.55, metalness: 0.35 };
    }
  } else if (/lemn|wood|hpl|cedru|timber|teak|larice|larch/.test(m)) {
    // SCÂNDURI orizontale de 15 cm, cu rost de 5 mm; o repetare = 1,2 m. Un
    // câmp de HPL «cedru» cădea pe ramura de piatră (cheia conține «placaj»)
    // și ieșea ca gresie gri, nu ca lemn (Test-Catalin-v1, 28.09.2026).
    const p = panza(512, 512);
    if (p) {
      const [c, ctx] = p;
      const s = 512;
      ctx.fillStyle = amesteca(culoare, 0.55); ctx.fillRect(0, 0, s, s);      // rostul, în umbră
      const bh = 0.15 / 1.2 * s, rost = 0.005 / 1.2 * s;
      for (let r = 0, y = 0; y < s; r++, y += bh) {
        const ton = 0.9 + 0.2 * zgomot(r * 7.1, 3.3);
        const g = ctx.createLinearGradient(0, y, s, y);
        g.addColorStop(0, amesteca(culoare, ton));
        g.addColorStop(0.5, amesteca(culoare, ton * 1.06));
        g.addColorStop(1, amesteca(culoare, ton * 0.96));
        ctx.fillStyle = g; ctx.fillRect(0, y + rost, s, bh - 2 * rost);
        // fibra: câteva linii subțiri, puțin mai închise
        ctx.fillStyle = amesteca(culoare, ton * 0.88);
        for (let k = 0; k < 3; k++) {
          const yy = y + rost + (bh - 2 * rost) * (0.2 + 0.3 * k + 0.1 * zgomot(k, r));
          ctx.fillRect(0, yy, s, 1);
        }
      }
      const t = new THREE.CanvasTexture(c);
      out = { map: t, latura_m: 1.2, roughness: 0.7, metalness: 0 };
    }
  } else if (/piatra|stone|placaj|dale/.test(m)) {
    // PLĂCI MARI, PUȚIN CONTRASTATE: 120 × 60 cm în țesătură, o repetare =
    // 2,4 m. Cu plăci de 60 × 30 și nuanțe care săreau cu ±8 % de la una la
    // alta, calcarul deschis al referinței ieșea ca o baie de gresie gri
    // (Test-Catalin-v1, 28.09.2026); piatra de fațadă e placă mare, aproape
    // uniformă, cu rostul abia citit.
    const p = panza(512, 512);
    if (p) {
      const [c, ctx] = p;
      const s = 512;
      ctx.fillStyle = amesteca(culoare, 0.86); ctx.fillRect(0, 0, s, s);
      const bw = 1.2 / 2.4 * s, bh = 0.6 / 2.4 * s, rost = 0.005 / 2.4 * s;
      for (let r = 0, y = 0; y < s; r++, y += bh) {
        const dec = (r % 2) * bw / 2;
        for (let x = -bw; x < s + bw; x += bw) {
          ctx.fillStyle = amesteca(culoare, 0.97 + 0.05 * zgomot(x + dec, y));
          ctx.fillRect(x + dec + rost, y + rost, bw - 2 * rost, bh - 2 * rost);
        }
      }
      const t = new THREE.CanvasTexture(c);
      out = { map: t, latura_m: 2.4, roughness: 0.55, metalness: 0.03 };
    }
  } else if (/tencuiala|plaster|render/.test(m)) {
    // granulație fină; o repetare = 1 m
    const p = panza(256, 256);
    if (p) {
      const [c, ctx] = p;
      const img = ctx.createImageData(256, 256);
      const r = (culoare >> 16) & 255, g = (culoare >> 8) & 255, b = culoare & 255;
      for (let i = 0; i < 256 * 256; i++) {
        const k = 0.94 + 0.12 * zgomot(i % 256, Math.floor(i / 256));
        img.data[i * 4] = Math.min(255, r * k); img.data[i * 4 + 1] = Math.min(255, g * k);
        img.data[i * 4 + 2] = Math.min(255, b * k); img.data[i * 4 + 3] = 255;
      }
      ctx.putImageData(img, 0, 0);
      const t = new THREE.CanvasTexture(c);
      out = { map: t, latura_m: 1.0, roughness: 0.9, metalness: 0 };
    }
  }
  if (out) {
    out.map.wrapS = out.map.wrapT = THREE.RepeatWrapping;
    out.map.repeat.set(1 / out.latura_m, 1 / out.latura_m);
    out.map.colorSpace = THREE.SRGBColorSpace;
    out.map.anisotropy = 8;
  }
  _cache.set(cheie, out);
  return out;
}


/** Un ORNAMENT CU VOLUM: secțiunea `(d, z)` în metri — `d` de la planul
 *  `baza` spre afară, `z` de la `z0` în sus — măturată de-a lungul feței de la
 *  `a` la `b`. Un solid adevărat: o cornișă are intradosul teșit, se vede de
 *  jos, aruncă umbră.
 *
 *  CAPETELE SE TAIE ÎN UNGHI, nu drept: la un colț, profilul de pe o față se
 *  întâlnește cu cel de pe fața vecină pe planul bisector, ca două baghete de
 *  ramă. `kA`/`kB` spun cât se mută capătul pe `u` la fiecare metru de
 *  adâncime: capătul b stă la `L + kB·d`, capătul a la `kA·d`. La un colț
 *  drept ieșind kB = +1 (fața exterioară a profilului ajunge cu `d` peste
 *  colț, iar la zidărie rămâne exact în colț) și, simetric, kA = −1; la unul
 *  intrând semnele se inversează; 0 = capăt drept. Aceeași formulă dă ambele
 *  capete: `(n_vecin·u)/(1 + n·n_vecin)`.
 *  Cele două profile vecine cu aceeași bază se ating fără să se
 *  suprapună — nicio față comună în același plan, deci nimic care să se bată
 *  la colț — și fără ciot: o prelungire dreaptă de 20 cm ieșea în aer lângă
 *  fața vecină, ca un bloc lipit de colț.
 *
 *  Geometria se construiește direct (patrulatere laterale + capacele
 *  triangulate), nu prin `ExtrudeGeometry`, care nu știe capete oblice. */
export function profilPeFata(a: number[], b: number[], n: number[], z0: number,
                             baza: number, sectiune: number[][], zBaza: number,
                             kA = 0, kB = 0): THREE.BufferGeometry | null {
  const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (L < 0.05 || sectiune.length < 3) return null;
  const ux = (b[0] - a[0]) / L, uy = (b[1] - a[1]) / L;
  const m = sectiune.length;
  // punctul (i) al secțiunii la capătul `cap` (0 = a, 1 = b)
  const punct = (i: number, cap: 0 | 1): number[] => {
    const [d, z] = sectiune[i];
    const u = cap ? L + kB * d : kA * d;
    const dd = baza + d;
    return [a[0] + ux * u + n[0] * dd, a[1] + uy * u + n[1] * dd, zBaza + z0 + z];
  };
  const poz: number[] = [];
  const tri = (p: number[], q: number[], r: number[], afara: number[]) => {
    // (q−p)×(r−p) trebuie să dea spre `afara`; altfel se inversează
    const e1 = [q[0] - p[0], q[1] - p[1], q[2] - p[2]], e2 = [r[0] - p[0], r[1] - p[1], r[2] - p[2]];
    const nx = e1[1] * e2[2] - e1[2] * e2[1], ny = e1[2] * e2[0] - e1[0] * e2[2],
          nz = e1[0] * e2[1] - e1[1] * e2[0];
    if (nx * afara[0] + ny * afara[1] + nz * afara[2] >= 0) poz.push(...p, ...q, ...r);
    else poz.push(...p, ...r, ...q);
  };
  // laturile: pentru muchia i→i+1 a secțiunii, „afară" e normala ei spre
  // exteriorul poligonului, în planul (n, z) — din SENSUL de parcurgere, nu
  // dinspre centru: pe o secțiune concavă (glaful cu lăcrimare, un U întors)
  // centrul cade în afara pereților lăcrimarelor și fețele lor ieșeau întoarse
  let arieSemn = 0;
  for (let i = 0; i < m; i++) {
    const j = (i + 1) % m;
    arieSemn += sectiune[i][0] * sectiune[j][1] - sectiune[j][0] * sectiune[i][1];
  }
  const sens = arieSemn >= 0 ? 1 : -1;
  for (let i = 0; i < m; i++) {
    const j = (i + 1) % m;
    const md = (sectiune[j][1] - sectiune[i][1]) * sens, mz = -(sectiune[j][0] - sectiune[i][0]) * sens;
    const afara = [n[0] * md, n[1] * md, mz];
    const pa = punct(i, 0), pb = punct(i, 1), qa = punct(j, 0), qb = punct(j, 1);
    tri(pa, pb, qb, afara);
    tri(pa, qb, qa, afara);
  }
  // capacele: secțiunea triangulată, la a privind spre −u, la b spre +u
  const cont = sectiune.map(([d, z]) => new THREE.Vector2(d, z));
  const fete = THREE.ShapeUtils.triangulateShape(cont, []);
  for (const [i, j, k] of fete) {
    tri(punct(i, 0), punct(j, 0), punct(k, 0), [-ux, -uy, 0]);
    tri(punct(i, 1), punct(j, 1), punct(k, 1), [ux, uy, 0]);
  }
  // UV ÎN METRI, ca la cutiile panourilor (v. `WorldUVGenerator`): pe laturi
  // lungimea și cota, pe fețele culcate lungimea și adâncimea, pe capace
  // adâncimea și cota. Fără UV, profilul nu se putea uni cu cutiile din
  // aceeași grupă de material (brâul `band` fără secțiune are UV):
  // `mergeGeometries` întorcea `null` și grupa ÎNTREAGĂ lipsea din machetă,
  // iar fanta tăiată în finisaj pentru ea lăsa corpul la vedere la fiecare
  // planșeu (Demo-UTR6, 15.10.2026)
  const uv: number[] = [];
  for (let t = 0; t + 8 < poz.length; t += 9) {
    const e1 = [poz[t + 3] - poz[t], poz[t + 4] - poz[t + 1], poz[t + 5] - poz[t + 2]];
    const e2 = [poz[t + 6] - poz[t], poz[t + 7] - poz[t + 1], poz[t + 8] - poz[t + 2]];
    const nx = e1[1] * e2[2] - e1[2] * e2[1], ny = e1[2] * e2[0] - e1[0] * e2[2],
          nz = e1[0] * e2[1] - e1[1] * e2[0];
    const peU = Math.abs(nx * ux + ny * uy), peN = Math.abs(nx * n[0] + ny * n[1]);
    for (let k = t; k < t + 9; k += 3) {
      const dx = poz[k] - a[0], dy = poz[k + 1] - a[1], z = poz[k + 2];
      const lung = dx * ux + dy * uy, adanc = dx * n[0] + dy * n[1];
      if (Math.abs(nz) >= Math.max(peU, peN)) uv.push(lung, adanc);
      else if (peU > peN) uv.push(adanc, z);
      else uv.push(lung, z);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(poz, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  geo.computeVertexNormals();
  return geo;
}

/** Geometriile unei grupe de material, potrivite ca să se poată uni:
 *  `mergeGeometries` cere aceleași atribute peste tot și toate indexate sau
 *  niciuna, altfel întoarce `null` — și o grupă întreagă (toate brâiele unei
 *  culori, de pe toate corpurile) dispărea din machetă fără urmă. Unde lipsește
 *  `uv` se pune zero (culoarea plată a texturii), `normal` se calculează, iar
 *  orice alt atribut care nu e peste tot se scoate. */
export function potrivitePentruUnire(list: THREE.BufferGeometry[]): THREE.BufferGeometry[] {
  if (list.length < 2) return list;
  const indexate = list.filter((g) => g.index).length;
  let out = indexate && indexate < list.length
    ? list.map((g) => (g.index ? g.toNonIndexed() : g)) : list;
  const nume = new Set<string>();
  for (const g of out) for (const k of Object.keys(g.attributes)) nume.add(k);
  out = out.map((g) => {
    for (const k of nume) {
      if (g.getAttribute(k)) continue;
      const n = g.getAttribute("position").count;
      if (k === "uv") g.setAttribute("uv", new THREE.Float32BufferAttribute(new Float32Array(n * 2), 2));
      else if (k === "normal") g.computeVertexNormals();
    }
    return g;
  });
  for (const k of nume) {
    if (out.every((g) => g.getAttribute(k))) continue;
    for (const g of out) g.deleteAttribute(k);
  }
  return out;
}
